/**
 * Recency facts for the favored-item selector (utils/favoredItem).
 *
 * The selector is pure; this service assembles its three usage signals for
 * the items that belong to working-set groups:
 *
 *   entered — rides on the purchase options the caller already fetched
 *             (purchase_options.last_operational_source_at)
 *   counted — latest count_items.counted_at per member, any session
 *   ordered — latest purchase_order_items.created_at reached through
 *             purchase_order_items.purchase_option_id (the option names the
 *             member whose recency the order really is)
 *
 * Failure posture: every fetch here is advisory. A failed query returns an
 * empty map and the selector falls through to its deterministic tiebreaks
 * (newest option → cheapest comparable → lowest id) — group representation
 * must never break because a recency lookup did.
 */
import sharedSupabase from '../config/supabase';
import { favoredByGroup } from '../utils/favoredItem';

const uniqueIds = (itemIds) => [...new Set(
  (Array.isArray(itemIds) ? itemIds : [])
    .filter((id) => id !== null && id !== undefined)
    .map(String),
)];

/**
 * Latest counted_at per inventory item, store-scoped through count_sessions.
 * @returns {Promise<Map<string, string>>} item id → ISO instant
 */
export async function fetchLastCountedAtForItems(storeId, itemIds, client = sharedSupabase) {
  const ids = uniqueIds(itemIds);
  if (!storeId || ids.length === 0) return new Map();
  try {
    const { data, error } = await client
      .from('count_items')
      .select('inventory_item_id, counted_at, count_sessions!inner(store_id)')
      .eq('count_sessions.store_id', storeId)
      .in('inventory_item_id', ids)
      .not('counted_at', 'is', null)
      .order('counted_at', { ascending: false })
      .limit(1000);
    if (error) throw new Error(error.message);
    const byItem = new Map();
    (data || []).forEach((row) => {
      const key = String(row?.inventory_item_id ?? '');
      if (!key || byItem.has(key)) return; // descending order → first is latest
      if (row?.counted_at) byItem.set(key, row.counted_at);
    });
    return byItem;
  } catch (err) {
    console.warn('[favored-item] counted recency unavailable:', err?.message || err);
    return new Map();
  }
}

/**
 * Latest PO-line instant per inventory item, reached through the line's
 * purchase_option_id → the option's owning item. Lines with no option on
 * record (legacy rows, hand-typed lines) carry no member recency and are
 * skipped by the query itself.
 * @returns {Promise<Map<string, string>>} item id → ISO instant
 */
export async function fetchLastOrderedAtForItems(storeId, itemIds, client = sharedSupabase) {
  const ids = uniqueIds(itemIds);
  if (!storeId || ids.length === 0) return new Map();
  try {
    const { data, error } = await client
      .from('purchase_order_items')
      .select('created_at, option:purchase_options!inner(inventory_item_id, store_id)')
      .eq('option.store_id', storeId)
      .in('option.inventory_item_id', ids)
      .order('created_at', { ascending: false })
      .limit(1000);
    if (error) throw new Error(error.message);
    const byItem = new Map();
    (data || []).forEach((row) => {
      const key = String(row?.option?.inventory_item_id ?? '');
      if (!key || byItem.has(key)) return;
      if (row?.created_at) byItem.set(key, row.created_at);
    });
    return byItem;
  } catch (err) {
    console.warn('[favored-item] ordered recency unavailable:', err?.message || err);
    return new Map();
  }
}

/**
 * The favored member of every working-set group, composed from the caller's
 * option pools plus the two recency queries above.
 *
 * @param {object} args
 * @param {*} args.storeId
 * @param {{membersByGroupId: Map}} args.groups  buildWorkingSetGroups output
 * @param {Object} [args.optionsByItemId]  item id → purchase option rows
 *   (should carry last_operational_source_at / created_at /
 *   normalized_unit_cost — purchaseOptionService selects all three)
 * @returns {Promise<Map<string, string>>} group id → favored member item id
 */
export async function resolveFavoredMembers({ storeId, groups, optionsByItemId = {} } = {}, client = sharedSupabase) {
  const membersByGroupId = groups?.membersByGroupId;
  if (!membersByGroupId?.size) return new Map();
  const memberIds = uniqueIds([...membersByGroupId.values()].flat());

  const [countedAt, orderedAt] = await Promise.all([
    fetchLastCountedAtForItems(storeId, memberIds, client),
    fetchLastOrderedAtForItems(storeId, memberIds, client),
  ]);

  return favoredByGroup(membersByGroupId, (itemId) => ({
    options: optionsByItemId?.[itemId] || optionsByItemId?.[Number(itemId)] || [],
    lastCountedAt: countedAt.get(itemId) ?? null,
    lastOrderedAt: orderedAt.get(itemId) ?? null,
  }));
}
